<template>
  <div v-if="!loading" class="flex flex-col">
    <div class="flex flex-col sm:flex-row sm:flex-wrap sm:space-x-4 sm:items-end mt-2 gap-y-2">
      <custom-search
        class="w-full sm:w-[400px]"
        placeholder="Buscar por email o nombre"
        v-model="searchText"
        @confirm="search"
        @clear="search"
      />

      <div class="flex flex-col">
        <label for="status_filter" class="text-sm">Estatus</label>
        <select
          id="status_filter"
          v-model="statusFilter"
          class="rounded border border-content-100 px-2 py-1"
          @change="search"
        >
          <option value="">Todos</option>
          <option value="pending">Pendiente</option>
          <option value="paid">Pagada</option>
          <option value="packing">Empacando</option>
          <option value="shipped">Enviada</option>
          <option value="delivered">Entregada</option>
          <option value="cancelled">Cancelada</option>
          <option value="refunded">Reembolsada</option>
        </select>
      </div>

      <div class="flex flex-col">
        <label for="date_from" class="text-sm">Desde</label>
        <input
          id="date_from"
          type="date"
          v-model="dateFrom"
          class="rounded border border-content-100 px-2 py-1"
          @change="search"
        />
      </div>

      <div class="flex flex-col">
        <label for="date_to" class="text-sm">Hasta</label>
        <input
          id="date_to"
          type="date"
          v-model="dateTo"
          class="rounded border border-content-100 px-2 py-1"
          @change="search"
        />
      </div>

      <label class="flex items-center space-x-2 text-sm pb-1">
        <input
          type="checkbox"
          v-model="needsInvoice"
          @change="search"
        />
        <span>Solo con factura</span>
      </label>
    </div>

    <div class="card mt-6">
      <table class="table-auto text-sm mt-2 bg-white w-full">
        <thead class="text-left">
          <tr class="bg-primary-100">
            <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">ID</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Fecha</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Total</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estatus</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Factura</th>
            <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in orders" :key="order.id" class="table-row">
            <td class="table-cell">#{{ order.id }}</td>
            <td class="table-cell">{{ formatDatetime(order.created_at) }}</td>
            <td class="table-cell">
              <div class="flex flex-col">
                <span v-if="order.client">
                  {{ order.client.name }} {{ order.client.last_name }}
                </span>
                <span v-else>
                  {{ order.guest_name }}
                  <span class="text-xs bg-content-100 text-content-400 px-1 rounded ml-1">invitado</span>
                </span>
                <span class="text-xs text-content-300">
                  {{ order.client?.user?.email || order.guest_email }}
                </span>
              </div>
            </td>
            <td class="table-cell">{{ integerToCurrency(order.total) }}</td>
            <td class="table-cell">
              <span
                :class="[
                  'p-1 text-xs rounded text-white w-fit inline-block',
                  statusColor(order.status),
                ]"
              >{{ statusLabel(order.status) }}</span>
            </td>
            <td class="table-cell">
              <span
                v-if="order.needs_invoice"
                class="p-1 text-xs rounded bg-tertiary-500 text-white w-fit inline-block"
              >Factura</span>
              <span v-else class="text-content-300 text-xs">—</span>
            </td>
            <td class="table-cell">
              <router-link
                :to="{ name: 'shopOrdersDetail', params: { id: order.id } }"
                class="secondary-button"
              >Detalle</router-link>
            </td>
          </tr>
          <tr v-if="!orders.length">
            <td colspan="7" class="text-center p-4 text-content-300">
              No hay pedidos que mostrar.
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />
  </div>

  <div v-else class="flex w-full h-full items-center">
    <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
  </div>
</template>

<script>
import api from "@/api";
import Pagination from "@/components/ui/Pagination.vue";
import { formatDatetime } from "@/utils/dateFormatter";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Shop Orders",

  components: { Pagination },

  data() {
    return {
      orders: [],
      loading: false,
      currentPage: 1,
      totalPages: 0,
      searchText: "",
      statusFilter: "",
      dateFrom: "",
      dateTo: "",
      needsInvoice: false,
    };
  },

  created() {
    this.$store.commit("setStoreSelectedTab", "shopOrders");
    this.loadOrders();
  },

  methods: {
    formatDatetime,
    integerToCurrency,

    statusLabel(status) {
      const map = {
        pending: "Pendiente",
        paid: "Pagada",
        packing: "Empacando",
        shipped: "Enviada",
        delivered: "Entregada",
        cancelled: "Cancelada",
        refunded: "Reembolsada",
      };
      return map[status] || status;
    },

    statusColor(status) {
      const map = {
        pending: "bg-content-300",
        paid: "bg-success-500",
        packing: "bg-orange-500",
        shipped: "bg-primary-500",
        delivered: "bg-success-700",
        cancelled: "bg-red-500",
        refunded: "bg-content-400",
      };
      return map[status] || "bg-content-300";
    },

    async loadOrders() {
      this.loading = true;

      try {
        const response = await api.shopOrders.list({
          page: this.currentPage,
          search: this.searchText,
          status: this.statusFilter,
          date_from: this.dateFrom,
          date_to: this.dateTo,
          needs_invoice: this.needsInvoice ? "true" : "",
        });
        this.orders = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.last_page || 0;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    search() {
      this.currentPage = 1;
      this.loadOrders();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadOrders();
    },
  },
};
</script>
